import { Check, ChevronRight } from "lucide-react";
import { Link, useParams } from "react-router";
import { AuthorByline } from "@client/components/author/AuthorByline";
import { ProductCurriculum } from "@client/components/product/ProductCurriculum";
import { ProductPurchasePanel } from "@client/components/product/ProductPurchasePanel";
import { ProductTeaser } from "@client/components/product/ProductTeaser";
import { RichContent } from "@client/components/content/RichContent";
import { PageMeta } from "@client/components/seo/PageMeta";
import { PageLoader } from "@client/components/ui/PageLoader";
import { ErrorState, NotFoundState } from "@client/components/ui/StateMessages";
import { useProduct } from "@client/hooks/use-catalog";
import { isNotFound } from "@client/lib/api-result";
import { catalogCopy } from "@client/locales/id/catalog";

const copy = catalogCopy.detail;

export function ProductDetailPage() {
  const { slug = "" } = useParams();
  const { data: product, isPending, error, refetch } = useProduct(slug);

  if (isPending) return <PageLoader />;
  if (isNotFound(error)) {
    return (
      <NotFoundState
        title={copy.notFoundTitle}
        description={copy.notFoundDescription}
        backHref="/#katalog"
        backLabel={copy.backToCatalog}
      />
    );
  }
  if (!product) return <main className="mx-auto max-w-6xl px-4 py-16"><ErrorState onRetry={() => void refetch()} /></main>;

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 sm:px-6 md:py-16">
      <PageMeta
        title={product.seoTitle ?? product.title}
        description={product.seoDescription ?? product.shortDescription}
        path={`/course/${product.slug}`}
        imageUrl={product.imageUrl}
      />

      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-foreground-muted">
        <Link to="/" className="hover:text-foreground">{copy.breadcrumbHome}</Link>
        <ChevronRight className="size-3.5" aria-hidden="true" />
        <Link to="/#katalog" className="hover:text-foreground">{copy.breadcrumbCatalog}</Link>
      </nav>

      <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="flex min-w-0 flex-col gap-10">
          <header className="animate-fade-up flex flex-col gap-4">
            <span className="font-mono text-xs tracking-widest text-accent-bright uppercase">
              {catalogCopy.productTypes[product.type]}
            </span>
            <h1 className="text-gradient text-3xl leading-tight font-semibold tracking-tight md:text-5xl">
              {product.title}
            </h1>
            {product.shortDescription && (
              <p className="text-lg leading-relaxed text-foreground-muted">{product.shortDescription}</p>
            )}
            {product.author && <AuthorByline author={product.author} />}
          </header>

          {product.teaserVideoId ? (
            <ProductTeaser videoId={product.teaserVideoId} title={product.title} posterUrl={product.imageUrl} />
          ) : product.imageUrl && (
            <img
              src={product.imageUrl}
              alt={product.title}
              width={1280}
              height={720}
              className="aspect-video w-full rounded-2xl border border-border-default object-cover shadow-card"
            />
          )}

          {/* On mobile the purchase panel sits right under the hero instead of at the bottom. */}
          <div className="lg:hidden">
            <ProductPurchasePanel product={product} />
          </div>

          {product.benefits.length > 0 && (
            <section>
              <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{copy.benefitsTitle}</h2>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {product.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3 rounded-xl border border-border-default bg-surface p-4 text-sm">
                    <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <ProductCurriculum items={product.curriculum} />

          {product.descriptionHtml && (
            <section>
              <h2 className="text-xl font-semibold tracking-tight md:text-2xl">{copy.descriptionTitle}</h2>
              <RichContent html={product.descriptionHtml} className="mt-5" />
            </section>
          )}
        </div>

        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ProductPurchasePanel product={product} />
          </div>
        </aside>
      </div>
    </main>
  );
}
